import { Alert, Stack } from '@mui/material'
import { ContainerPositionType } from '@operations/app/api'
import { IContainerPositionRadioOption } from '@operations/app/models/operator-pages'
import { EquipmentTypeIcon } from '@operations/features/equipmentPlanning/components/atoms/EquipmentTypeIcon'
import { useTranslate } from '@tolgee/react'
import { OperatorWithYardSelector } from '../../models/yard-selector.model'
import { ContainerPositionIcon } from './ContainerPositionIcon'
import { ContainerPositionRadioOption } from './ContainerPositionRadioOption'
import { ContainerPositionRadioYardOption } from './ContainerPositionRadioYardOption'

interface Props extends OperatorWithYardSelector {
  equipmentId?: number
  equipments: IContainerPositionRadioOption[]
  hasError?: boolean
  showVessel?: boolean
  showBerth?: boolean
  containerPositionType?: ContainerPositionType | undefined
  craneName?: string
  hasYardDestination?: boolean
  orderId?: number | null
  cargoUnitNumber?: string | null
  journeyId?: number | null
  onPositionChange?: (hasSlotSelected: boolean) => void
  onBlockingIssueChange?: (hasBlockingIssue: boolean) => void
  onDestinationChange: (
    id?: number | undefined,
    containerPosition?: ContainerPositionType | undefined,
  ) => void
}

export const ContainerPositionRadio = ({
  equipmentId,
  equipments,
  hasError,
  showVessel,
  showBerth,
  containerPositionType,
  craneName,
  hasYardDestination,
  orderId,
  cargoUnitNumber,
  journeyId,
  yardSelector,
  onPositionChange,
  onBlockingIssueChange,
  onDestinationChange,
}: Props) => {
  const { t } = useTranslate()

  const getTitle = (type: ContainerPositionType) => {
    if (type === ContainerPositionType.Berth) {
      return `${t('containerPositionTypes.berth', 'Berth')} - ${craneName}`
    }
    return t('vessel', 'Vessel')
  }

  return (
    <Stack gap={theme => theme.customSpacing.xs}>
      {hasError && (
        <Alert severity='error'>
          {t(
            'pleaseAssignAnOptionFromTheFollowingList',
            'Please assign an option from the following list',
          )}
        </Alert>
      )}
      {showVessel && (
        <ContainerPositionRadioOption
          key={ContainerPositionType.Vessel}
          id={ContainerPositionType.Vessel}
          title={getTitle(ContainerPositionType.Vessel)}
          selectedOptionId={equipmentId ?? containerPositionType}
          hasError={hasError}
          onChange={(id: number | ContainerPositionType | undefined) =>
            onDestinationChange(undefined, id as ContainerPositionType | undefined)
          }
          dataCy={`equipment-assignable-for-job-${ContainerPositionType.Vessel.toLowerCase()}`}
          icon={
            <ContainerPositionIcon type={ContainerPositionType.Vessel} backgroundColorTransparent />
          }
        />
      )}
      {showBerth && (
        <ContainerPositionRadioOption
          key={ContainerPositionType.Berth}
          id={ContainerPositionType.Berth}
          title={getTitle(ContainerPositionType.Berth)}
          selectedOptionId={equipmentId ?? containerPositionType}
          hasError={hasError}
          onChange={(id: number | ContainerPositionType | undefined) =>
            onDestinationChange(undefined, id as ContainerPositionType | undefined)
          }
          dataCy={`equipment-assignable-for-job-${ContainerPositionType.Berth.toLowerCase()}`}
          icon={
            <ContainerPositionIcon type={ContainerPositionType.Berth} backgroundColorTransparent />
          }
        />
      )}
      {equipments.map(equipment => (
        <ContainerPositionRadioOption
          key={equipment.id}
          id={equipment.id}
          title={equipment.title}
          subTitle={equipment.subtitle}
          selectedOptionId={equipmentId ?? containerPositionType}
          hasError={hasError}
          onChange={(id: number | ContainerPositionType | undefined) =>
            onDestinationChange(id as number, undefined)
          }
          dataCy={`equipment-assignable-for-job-${equipment.title.toLowerCase().trim()}`}
          icon={<EquipmentTypeIcon type={equipment.type} backgroundColorTransparent />}
        />
      ))}

      {hasYardDestination && (
        <ContainerPositionRadioYardOption
          id={ContainerPositionType.Yard}
          title={t(
            'enterUpdatedPositionOfTheContainerOnTheYard',
            'Enter updated position of the container on the yard',
          )}
          selectedOptionId={containerPositionType}
          hasError={hasError}
          cargoUnitNumber={cargoUnitNumber!}
          orderId={orderId}
          dataCy='equipment-assignable-for-job-yard'
          journeyId={journeyId}
          yardSelector={yardSelector}
          onChange={(position?: ContainerPositionType) => onDestinationChange(undefined, position)}
          onPositionChange={onPositionChange}
          onBlockingIssueChange={onBlockingIssueChange}
        />
      )}
    </Stack>
  )
}
